/**
 * NAP — Name, Address, Phone.
 *
 * Single source of truth for the business entity. Every place that states who
 * we are (footer, contact page, JSON-LD, llms.txt, the prerendered HTML) reads
 * from here, so the strings are byte-identical everywhere on the site.
 *
 * Why it matters: Google, and every answer engine that builds an entity graph
 * on top of it, matches a business across the web by exact-string agreement on
 * name + address + phone. One page saying "برج الزعيم، الطابق 25" while the
 * schema says "Sheikh Zayed Road" splits the entity in two and weakens every
 * citation. The values below must match the DLD/RERA broker record (No. 96129)
 * and the Google Business Profile character for character.
 *
 * ⚠ Before deploying: verify STREET_* and the office address against the
 *   official DLD trade-licence record. Two conflicting addresses existed in the
 *   codebase; the Sheikh Zayed Road one was kept because it appeared in three
 *   places against the tower address's one.
 */

export const NAP = {
    // ── Name ──────────────────────────────────────────────────────────────
    legalName: 'Al Zaeem Al Ainawi Real Estate L.L.C',
    name: {
        en: 'Al Zaeem Al Ainawi Real Estate',
        ar: 'شركة الزعيم العيناوي العقارية',
        ru: 'Al Zaeem Al Ainawi Real Estate',
        zh: 'Al Zaeem Al Ainawi Real Estate',
        ur: 'Al Zaeem Al Ainawi Real Estate'
    },
    alternateName: [
        'Al Zaeem',
        'Al Zaeem Al Ainawi',
        'Trust Ainawy',
        'الزعيم العيناوي',
        'الزعيم العيناوي العقارية',
        'شركة الزعيم العيناوي العقارية',
        'الزعيم العيناوي للعقارات'
    ],

    // ── Address ───────────────────────────────────────────────────────────
    address: {
        street: {
            en: 'Sheikh Zayed Road — Majlis Al Zaeem',
            ar: 'شارع الشيخ زايد — مجلس الزعيم'
        },
        locality: { en: 'Dubai', ar: 'دبي' },
        region: { en: 'Dubai', ar: 'دبي' },
        country: { en: 'United Arab Emirates', ar: 'الإمارات العربية المتحدة' },
        countryCode: 'AE',
        // One-line form, used verbatim in the footer and in llms.txt.
        full: {
            en: 'Sheikh Zayed Road — Majlis Al Zaeem, Dubai, United Arab Emirates',
            ar: 'شارع الشيخ زايد — مجلس الزعيم، دبي، الإمارات العربية المتحدة'
        }
    },
    geo: { lat: 25.2048, lng: 55.2708 },

    // ── Phone ─────────────────────────────────────────────────────────────
    // E.164 for schema/tel:, display form for humans. Never invent a third.
    phone: '+971521138999',
    phoneDisplay: '+971 52 113 8999',
    whatsapp: '971521138999',
    whatsappBusinessLink: 'https://wa.me/message/D3DC5NKCUUWIL1',
    email: 'info@trustainawy.com',

    // ── Licensing — the trust signal answer engines look for ──────────────
    dldBrokerNumber: '96129',
    regulator: {
        en: 'Dubai Land Department (DLD) — RERA',
        ar: 'دائرة الأراضي والأملاك بدبي — مؤسسة التنظيم العقاري (ريرا)'
    },

    // ── Presence ──────────────────────────────────────────────────────────
    url: 'https://trustainawy.com',
    sameAs: [
        'https://www.instagram.com/trust_ainawy',
        'https://youtube.com/@trust_ainawy',
        'https://www.tiktok.com/@trust_ainawy',
        'https://www.threads.com/@trust_ainawy',
        'https://x.com/trust_ainawy',
        'https://wa.me/971521138999'
    ],

    openingHours: {
        // Sat–Thu 09:00–18:00, closed Friday.
        spec: [{ days: ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'], opens: '09:00', closes: '18:00' }],
        display: { en: 'Saturday – Thursday: 9:00 AM – 6:00 PM', ar: 'السبت – الخميس: 9 صباحاً – 6 مساءً' }
    },

    areaServed: ['Dubai', 'Abu Dhabi'],
    languages: ['ar', 'en', 'ru', 'zh', 'ur']
};

/** Picks a localised field, falling back to English. */
export const nap = (field, lang = 'en') =>
    (field && (field[lang] ?? field.en)) ?? '';

export default NAP;
